import React, { useState } from 'react';

import { TextInput } from '@components/admin/Forms';
import { EmailDuplicateDialog } from '@components/admin/members/EmailDuplicateDialog';
import { SubmittingOverlay } from '@components/Forms/SubmittingOverlay';
import { SpinnerModal } from '@components/SpinnerModal';
import { cellFactory, Table } from '@components/Table/Table';
import { staffStrings } from '@data/staff';
import { usePatchCompanyStaff } from '@hooks/mutation/dashboard/usePatchCompanyStaff';
import { useResendEmail } from '@hooks/mutation/dashboard/useResendEmail';
import { useCountDown } from '@hooks/useCountDown';
import { AdminUser } from '@models/next/User';
import { dashboardMemberStaffForm } from '@models/User';
import { makeMuiStyles, useCommonStyles } from '@styles/index';
import { EmailDuplicateError, FormError } from '@utils/errors';
import { createValidate, formStrings } from '@utils/forms';
import clsx from 'clsx';
import { Formik } from 'formik';

const useStyles = makeMuiStyles((_theme, common) => ({
  nameInputContainer: {
    ...common.flexInline,
    alignItems: 'center',
    width: '200px',
  },
  nameInput: {
    width: '80px',
  },
}));

export interface MemberStaffDisplayEditRowProps {
  index: number;
  item: AdminUser;
  editing: boolean;
  companyId: number;
  onEditingChange: (editing: boolean) => void;
}

const validate = createValidate(dashboardMemberStaffForm);

export const MemberStaffDisplayEditRow: React.FC<MemberStaffDisplayEditRowProps> = (props) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { index, item, editing, companyId, onEditingChange } = props;

  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const patchCompanyStaff = usePatchCompanyStaff(companyId);
  const resendEmail = useResendEmail();
  const countdown = useCountDown(60);

  const initialValues = {
    last_name: item.last_name ?? '',
    first_name: item.first_name ?? '',
    cellphone: item.cellphone ?? '',
    landline: item.landline ?? '',
    email: item.email ?? '',
  };

  if (editing) {
    const renderInput = (name: string, disabled?: boolean) => {
      return <TextInput noLabel name={name} disabled={disabled} />;
    };

    return (
      <Formik
        initialValues={initialValues}
        validate={validate}
        onSubmit={async (values) => {
          try {
            await patchCompanyStaff
              .mutateAsync({
                id: item.id,
                body: values,
              })
              .then(() => onEditingChange(false));
          } catch (e) {
            if (e instanceof EmailDuplicateError) {
              setEmailDialogOpen(true);
            } else if (e instanceof FormError) {
              alert(e.getDisplayMessage());
            } else {
              alert(String(e));
            }
          }
        }}
      >
        {({ submitForm }) => (
          <>
            <EmailDuplicateDialog
              open={emailDialogOpen}
              onClose={() => setEmailDialogOpen(false)}
            />
            <SubmittingOverlay />
            <Table.Row
              columns={[
                cellFactory.create(index + 1),
                cellFactory.create(
                  <div className={classes.nameInputContainer}>
                    <TextInput noLabel name={'last_name'} classes={{ input: classes.nameInput }} />
                    <div style={{ width: '10px' }} />
                    <TextInput noLabel name={'first_name'} classes={{ input: classes.nameInput }} />
                  </div>,
                ),
                cellFactory.create(renderInput('cellphone')),
                cellFactory.create(renderInput('landline')),
                cellFactory.create(renderInput('email')),
                cellFactory.create(
                  <a
                    className={clsx(classes.link, classes.baseButton, classes.whitespacePre)}
                    onClick={() => submitForm()}
                  >
                    {formStrings.update}
                  </a>,
                ),
              ]}
            />
          </>
        )}
      </Formik>
    );
  } else {
    return (
      <>
        <SpinnerModal open={resendEmail.isLoading} />
        <Table.Row
          columns={[
            cellFactory.create(index + 1),
            cellFactory.create(`${item.last_name} ${item.first_name}` || '-'),
            cellFactory.create(item.cellphone || '-'),
            cellFactory.create(item.landline || '-'),
            cellFactory.create(
              <>
                {item.email || '-'}
                {item.email_verified && item.email ? null : (
                  <>
                    （{staffStrings.emailNotVerified}）
                    {countdown.isCounting ? (
                      <span className={clsx(classes.textGray500, classes.underline)}>
                        {staffStrings.resendEmail}({countdown.timeLeft}s)
                      </span>
                    ) : (
                      <a
                        className={clsx(classes.link, classes.underline)}
                        onClick={() => {
                          resendEmail
                            .mutateAsync({ email: item.email })
                            .then(() => countdown.start());
                        }}
                      >
                        {staffStrings.resendEmail}
                      </a>
                    )}
                  </>
                )}
              </>,
            ),
            cellFactory.create(
              <a
                className={clsx(classes.link, classes.baseButton, classes.whitespacePre)}
                onClick={() => onEditingChange(true)}
              >
                {formStrings.edit}
              </a>,
            ),
          ]}
        />
      </>
    );
  }
};
